import { ref, watch } from 'vue'
import type { TranslationOptions } from '~/types/translation'

export const defaultTranslationPrompt = `You are a professional translator with deep understanding of both Chinese and English cultures and contexts.

TRANSLATION PRINCIPLES:
1. Understand the complete meaning and context before translating
2. Focus on conveying the INTENT and MESSAGE, not literal word-for-word translation
3. Adapt idioms, expressions, and cultural references appropriately
4. Ensure the translation sounds natural and fluent to native English speakers
5. Maintain the tone and style of the original text

SPECIFIC TERMINOLOGY TRANSLATIONS:
- 线路 → server (in the context of VPN/proxy services)
- 节点 → server or node (depending on context)
- 流量 → data or traffic (depending on context)
- 套餐 → plan or package
- 余额 → balance
- 充值 → top up or recharge
- 续费 → renew or renewal

For HTML content, preserve ALL HTML tags and attributes exactly as they are.
Return ONLY the translated text without any explanation.`

export function useTranslationTools() {
  const selectedModel = ref<string>('openai/gpt-5-chat')
  const customPrompt = ref<string>(defaultTranslationPrompt)

  // Restore settings
  if (useIsBrowser()) {
    const savedPrompt = localStorage.getItem('translationPrompt')
    const savedModel = localStorage.getItem('translationModel')
    if (savedPrompt) customPrompt.value = savedPrompt
    if (savedModel) selectedModel.value = savedModel
  }

  // Persist settings
  watch(customPrompt, (val) => {
    if (useIsBrowser()) localStorage.setItem('translationPrompt', val)
  })
  watch(selectedModel, (val) => {
    if (useIsBrowser()) localStorage.setItem('translationModel', val)
  })

  async function translateText(text: string, options?: TranslationOptions) {
    return useTranslation.translate(text, {
      sourceLanguage: options?.sourceLanguage || 'Chinese',
      targetLanguage: options?.targetLanguage || 'English',
      model: options?.model || selectedModel.value,
      customPrompt:
        options?.customPrompt !== undefined
          ? options.customPrompt
          : customPrompt.value !== defaultTranslationPrompt
            ? customPrompt.value
            : undefined,
    })
  }

  async function translateBatch(texts: { key: string; text: string }[], options?: TranslationOptions) {
    return useTranslation.translateBatch(texts, {
      sourceLanguage: options?.sourceLanguage || 'Chinese',
      targetLanguage: options?.targetLanguage || 'English',
      model: options?.model || selectedModel.value,
      customPrompt:
        options?.customPrompt !== undefined
          ? options.customPrompt
          : customPrompt.value !== defaultTranslationPrompt
            ? customPrompt.value
            : undefined,
    })
  }

  return {
    selectedModel,
    customPrompt,
    defaultTranslationPrompt,
    translateText,
    translateBatch,
  }
}

